import { cn } from "@/lib/utils";
import type { ChatEmptyStateElement } from "@/types/ui/branding";
import type { ConversationWelcomeProps } from "@/types/voice/chat-appearance";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const DEFAULT_ORDER: ChatEmptyStateElement[] = ["icon", "title", "subtitle"];

/** Website welcome screen, also rendered by the appearance editor's preview. */
export const ConversationWelcome = ({ brandColor, secondaryColor, agentName, logoUrl, chatStyle, textColor, darkSurface }: ConversationWelcomeProps) => (
  <div className="mx-auto flex w-full max-w-[860px] flex-col gap-3 px-5 py-8 sm:px-8">
    {(chatStyle?.elementOrder ?? DEFAULT_ORDER)
      .filter((element) => !chatStyle?.hiddenElements?.includes(element))
      .map((element) => {
        const align = chatStyle?.elementAlign?.[element] ?? "center";
        return (
          <div key={element} className={cn("flex min-w-0 flex-col", {
            "items-start text-left": align === "left",
            "items-center text-center": align === "center",
            "items-end text-right": align === "right",
          })}>
            {element === "icon" ? (
              <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl"
                style={{ background: `linear-gradient(135deg, ${brandColor}14, ${secondaryColor}14)` }}>
                {logoUrl ? <img src={logoUrl} alt="" className="h-10 w-10 object-contain" /> : (
                  <GenieIcon name="genie-lamp" size={28} color={brandColor} secondaryColor={secondaryColor} gradientEnabled />
                )}
              </span>
            ) : element === "title" ? (
              <h2
                className={cn(
                  "max-w-full break-words text-lg font-semibold sm:text-xl",
                  !textColor && (darkSurface ? "text-[#F1F5F9]" : "text-[#222B45]"),
                )}
                style={textColor ? { color: textColor } : undefined}
              >
                {chatStyle?.titleText || `Ask ${agentName || "your genie"} anything`}
              </h2>
            ) : (
              <p
                className={cn(
                  "max-w-[52ch] break-words text-sm leading-relaxed",
                  !textColor && (darkSurface ? "text-[#94A3B8]" : "text-[#64748B]"),
                )}
                style={textColor ? { color: `color-mix(in srgb, ${textColor} 70%, white)` } : undefined}
              >
                {chatStyle?.subtitleText || "Type a question to get started."}
              </p>
            )}
          </div>
        );
      })}
  </div>
);
